// ShopSeasonDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Offcanvas, Form, Button, ToggleButtonGroup, ToggleButton, Alert } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../assets/styles/ShopSeasonDrawer.css";

const NEED_VALUE = new Set(["FIXED", "PERCENTAGE"]);
const toServerSeason = (s) => (s ? String(s).toUpperCase() : "PERCENTAGE");

const parseNumberHuman = (v) => {
    if (v === null || v === undefined) return NaN;
    if (typeof v === "number") return v;
    return Number(String(v).replace(",", ".").trim());
};

const prettyModeLabel = (pricingMode) => {
    const m = String(pricingMode || "PERCENTAGE").toUpperCase();
    return m === "SEASONAL" ? "Seasonal (Medium/High)" : "Percentage";
};

const formatMonthDay = (month, day) => `${pad2(month)}-${pad2(day)}`;

const parseMonthDay = (value) => {
    const s = String(value || "").trim();
    const m = s.match(/^(\d{2})-(\d{2})$/);
    if (!m) return null;

    const month = Number(m[1]);
    const day = Number(m[2]);

    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;

    return { month, day };
};

/**
 * valueCents <-> UI conversions
 * - FIXED: UI shows EUR, server stores cents (integer)
 * - PERCENTAGE: UI shows percentage number, server stores the same number in valueCents
 *   (名字叫 valueCents 但先按你当前 DTO/字段走通)
 */
const fromServerValueCentsToUI = (season, valueCents) => {
    if (valueCents === null || valueCents === undefined) return "";
    const s = String(season || "").toUpperCase();

    if (s === "FIXED") return String(Number(valueCents) / 100);
    if (s === "PERCENTAGE") return String(valueCents);
    return String(valueCents);
};

const toServerValueCents = (season, uiNumber) => {
    const s = String(season || "").toUpperCase();
    if (uiNumber === null || uiNumber === undefined || Number.isNaN(uiNumber)) return undefined;

    if (s === "FIXED") return Math.round(uiNumber * 100);
    if (s === "PERCENTAGE") return uiNumber;
    return uiNumber;
};

/** ---------------- Timestamp without time zone helpers ----------------
 * DB: timestamp without time zone
 * FE: 传参/回填都使用 "YYYY-MM-DDTHH:mm:ss"（不带 Z、不带 offset）
 */
const pad2 = (n) => String(n).padStart(2, "0");

const parseTimestampNoTZ = (input) => {
    if (!input) return null;
    if (input instanceof Date) return new Date(input);

    const s = String(input).trim();
    // 兼容 "YYYY-MM-DDTHH:mm:ss" / "YYYY-MM-DD HH:mm:ss" / 只给日期
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (!m) {
        // 万一后端给了 ISO (带Z)，尽量兜底但不推荐
        const d = new Date(s);
        return isNaN(d) ? null : d;
    }

    const yyyy = Number(m[1]);
    const MM = Number(m[2]) - 1;
    const dd = Number(m[3]);
    const hh = m[4] != null ? Number(m[4]) : 0;
    const mm = m[5] != null ? Number(m[5]) : 0;
    const ss = m[6] != null ? Number(m[6]) : 0;

    // 本地构造，不涉及时区转换
    const d = new Date(yyyy, MM, dd, hh, mm, ss, 0);
    return isNaN(d) ? null : d;
};

const formatTimestampNoTZ = (date, { forceYear, setToMidnight = true } = {}) => {
    if (!date) return null;
    const d = new Date(date);

    const yyyy = forceYear ?? d.getFullYear();
    const MM = d.getMonth() + 1;
    const dd = d.getDate();

    let hh = d.getHours();
    let mm = d.getMinutes();
    let ss = d.getSeconds();

    // 你这类“日期区间规则”建议统一存 00:00:00
    if (setToMidnight) {
        hh = 0;
        mm = 0;
        ss = 0;
    }

    return `${yyyy}-${pad2(MM)}-${pad2(dd)}T${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
};

/** ---------- Recurring Month/Day Range Picker (UI hides year) ---------- */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const daysInMonth = (monthIndex) => new Date(2000, monthIndex + 1, 0).getDate();
const clampDay = (monthIndex, day) => Math.min(day, daysInMonth(monthIndex));

const RecurringMonthDayRange = ({
    startMonth,
    startDay,
    endMonth,
    endDay,
    onChange,
}) => {
    const safeStartDay = Math.min(startDay, daysInMonth(startMonth - 1));
    const safeEndDay = Math.min(endDay, daysInMonth(endMonth - 1));

    const spansAcrossYear =
        startMonth > endMonth || (startMonth === endMonth && safeStartDay > safeEndDay);

    return (
        <div>
            <div className="range-grid">
                <div className="range-label">From</div>
                <select
                    className="form-select form-select-sm"
                    value={startMonth}
                    onChange={(ev) =>
                        onChange({
                            startMonth: Number(ev.target.value),
                            startDay: Math.min(safeStartDay, daysInMonth(Number(ev.target.value) - 1)),
                            endMonth,
                            endDay: safeEndDay,
                        })
                    }
                >
                    {MONTHS.map((m, i) => (
                        <option key={m} value={i + 1}>
                            {m}
                        </option>
                    ))}
                </select>

                <select
                    className="form-select form-select-sm"
                    value={safeStartDay}
                    onChange={(ev) =>
                        onChange({
                            startMonth,
                            startDay: Number(ev.target.value),
                            endMonth,
                            endDay: safeEndDay,
                        })
                    }
                >
                    {Array.from({ length: daysInMonth(startMonth - 1) }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d}>
                            {d}
                        </option>
                    ))}
                </select>

                <div className="range-label">To</div>
                <select
                    className="form-select form-select-sm"
                    value={endMonth}
                    onChange={(ev) =>
                        onChange({
                            startMonth,
                            startDay: safeStartDay,
                            endMonth: Number(ev.target.value),
                            endDay: Math.min(safeEndDay, daysInMonth(Number(ev.target.value) - 1)),
                        })
                    }
                >
                    {MONTHS.map((m, i) => (
                        <option key={m} value={i + 1}>
                            {m}
                        </option>
                    ))}
                </select>

                <select
                    className="form-select form-select-sm"
                    value={safeEndDay}
                    onChange={(ev) =>
                        onChange({
                            startMonth,
                            startDay: safeStartDay,
                            endMonth,
                            endDay: Number(ev.target.value),
                        })
                    }
                >
                    {Array.from({ length: daysInMonth(endMonth - 1) }, (_, i) => i + 1).map((d) => (
                        <option key={d} value={d}>
                            {d}
                        </option>
                    ))}
                </select>
            </div>

            {spansAcrossYear && <div className="hint mt-2">Spans across the year (e.g., Aug → Mar).</div>}
        </div>
    );
};

const ShopSeasonDrawer = ({
    show,
    onClose,
    onSubmit,
    initialData,
    mode = "SEASON", // 'SEASON' | 'FIXED'
    pricingMode = "PERCENTAGE", // 'SEASONAL' | 'PERCENTAGE'
}) => {
    const isEditing = !!initialData;

    const allowedSeasons = useMemo(() => {
        if (mode === "FIXED") return ["FIXED"];
        if (mode === "SEASON") return ["MEDIUM", "HIGH"];
        return ["PERCENTAGE"];
    }, [mode]);

    const [isRecurring, setIsRecurring] = useState(true);

    // specific：真实年份日期（DatePicker 用）
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // recurring：固定 2000 年，仅月/日
    const [startMonth, setStartMonth] = useState(1);
    const [startDay, setStartDay] = useState(1);
    const [endMonth, setEndMonth] = useState(1);
    const [endDay, setEndDay] = useState(1);

    const [season, setSeason] = useState(allowedSeasons[0] || "PERCENTAGE");

    // UI string value (FIXED shows EUR; PERCENTAGE shows %)
    const [value, setValue] = useState("");

    // ✅ 初始化回填：用 parseTimestampNoTZ，避免浏览器/时区/locale 坑
    useEffect(() => {
        const init = initialData || null;

        const initIsRecurring = init ? !!init.isRecurring : true;
        setIsRecurring(initIsRecurring);

        if (initIsRecurring) {
            const s = parseMonthDay(init?.startDate);
            const e = parseMonthDay(init?.endDate);

            setStartDate(null);
            setEndDate(null);

            setStartMonth(s?.month || 1);
            setStartDay(s?.day || 1);
            setEndMonth(e?.month || 1);
            setEndDay(e?.day || 1);
        } else {
            const s = parseTimestampNoTZ(init?.startDate);
            const e = parseTimestampNoTZ(init?.endDate);

            setStartDate(s);
            setEndDate(e);

            setStartMonth(1);
            setStartDay(1);
            setEndMonth(1);
            setEndDay(1);
        }

        const s0 = toServerSeason(init?.season);
        const finalSeason = allowedSeasons.includes(s0) ? s0 : allowedSeasons[0] || "PERCENTAGE";
        setSeason(finalSeason);

        setValue(fromServerValueCentsToUI(finalSeason, init?.valueCents));
    }, [initialData, allowedSeasons]);

    const needsValue = NEED_VALUE.has(season);
    const numValue = needsValue ? parseNumberHuman(value) : 0;

    const dateValid = isRecurring
        ? !!startMonth && !!startDay && !!endMonth && !!endDay
        : !!startDate && !!endDate;

    const valueValid = !needsValue || (!Number.isNaN(numValue) && numValue >= 0);
    const canSubmit = dateValid && valueValid;

    const minDate = useMemo(() => new Date(new Date().getFullYear(), 0, 1), []);
    const maxDate = useMemo(() => new Date(new Date().getFullYear() + 4, 11, 31), []);

    const valueLabel = season === "PERCENTAGE" ? "Percentage (%)" : "Fixed Amount (EUR)";
    const valueHelp =
        season === "PERCENTAGE"
            ? "Example: 12.5 means +12.5% adjustment"
            : "This amount will be added on top of the base/event price.";
    const valueStep = season === "PERCENTAGE" ? 0.1 : 0.01;

    const title = mode === "FIXED"
        ? (isEditing ? "Edit fixed increase" : "Add fixed increase")
        : (mode === "PERCENTAGE"
            ? (isEditing ? "Edit price adjustment" : "Add price adjustment")
            : (isEditing ? "Edit season period" : "Add season period"));

    const handleConfirm = () => {
        if (!canSubmit) return;

        const payload = {
            ...(initialData || {}),
            isRecurring: !!isRecurring,
            startDate: isRecurring
                ? formatMonthDay(startMonth, startDay)
                : formatTimestampNoTZ(startDate, { setToMidnight: true }),
            endDate: isRecurring
                ? formatMonthDay(endMonth, endDay)
                : formatTimestampNoTZ(endDate, { setToMidnight: true }),
            season: toServerSeason(season),
            valueCents: NEED_VALUE.has(season) ? toServerValueCents(season, numValue) : undefined,
        };
        console.log(payload);
        onSubmit(payload);
        onClose();
    };

    return (
        <Offcanvas show={show} onHide={onClose} placement="end" backdrop scroll={false} className="season-drawer">
            <Offcanvas.Header closeButton className="drawer-header">
                <Offcanvas.Title>
                    <div className="drawer-title">{title}</div>
                    <div className="drawer-subtitle text-muted">
                        {mode === "SEASON" ? `Pricing mode: ${prettyModeLabel(pricingMode)}` : "Fixed increase rule"}
                    </div>
                </Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body className="drawer-body">
                {mode === "SEASON" && (
                    <Alert variant="info" className="py-2">
                        Current pricing mode: <strong>{prettyModeLabel(pricingMode)}</strong>. Available types are limited accordingly.
                    </Alert>
                )}

                {/* Occurrence */}
                <Form.Label className="field-label d-block mb-2">When does this apply?</Form.Label>
                <ToggleButtonGroup
                    type="radio"
                    name="occur"
                    value={isRecurring ? "yearly" : "specific"}
                    onChange={(v) => setIsRecurring(v === "yearly")}
                    className="segmented"
                >
                    <ToggleButton id="occur-specific" value="specific" variant="outline-secondary">
                        On specific dates
                    </ToggleButton>
                    <ToggleButton id="occur-yearly" value="yearly" variant="outline-secondary">
                        Repeats every year
                    </ToggleButton>
                </ToggleButtonGroup>

                {/* Date range */}
                <Form.Group className="mb-4 mt-3">
                    <Form.Label className="field-label d-block mb-2">Date range</Form.Label>

                    {isRecurring ? (
                        <RecurringMonthDayRange
                            startMonth={startMonth}
                            startDay={startDay}
                            endMonth={endMonth}
                            endDay={endDay}
                            onChange={({ startMonth, startDay, endMonth, endDay }) => {
                                setStartMonth(startMonth);
                                setStartDay(startDay);
                                setEndMonth(endMonth);
                                setEndDay(endDay);
                            }}
                        />
                    ) : (
                        <>
                            <DatePicker
                                selectsRange
                                startDate={startDate}
                                endDate={endDate}
                                onChange={(dates) => {
                                    const [s, e] = dates;
                                    setStartDate(s);
                                    setEndDate(e);
                                }}
                                dateFormat="yyyy/MM/dd"
                                placeholderText="Select start and end dates"
                                className="form-control"
                                dropdownMode="select"
                                minDate={minDate}
                                maxDate={maxDate}
                                // ✅ 禁止手输，避免解析错月/错日
                                onChangeRaw={(e) => e.preventDefault()}
                                // ✅ Offcanvas 遮挡问题：portal + 兜底 popper class
                                portalId="root-portal"
                                popperPlacement="bottom-start"
                                popperClassName="datepicker-popper"
                            />
                            {!dateValid && (
                                <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                                    Please select a start and end date.
                                </div>
                            )}
                        </>
                    )}
                </Form.Group>

                {/* Type */}
                <Form.Group className="mb-4">
                    <Form.Label className="field-label d-block mb-2">Type</Form.Label>
                    <ToggleButtonGroup
                        type="radio"
                        name="season"
                        value={season}
                        onChange={(val) => setSeason(val)}
                        className="segmented"
                    >
                        {allowedSeasons.includes("MEDIUM") && (
                            <ToggleButton id="type-medium" value="MEDIUM" variant="outline-secondary">
                                Medium
                            </ToggleButton>
                        )}
                        {allowedSeasons.includes("HIGH") && (
                            <ToggleButton id="type-high" value="HIGH" variant="outline-secondary">
                                High
                            </ToggleButton>
                        )}
                        {allowedSeasons.includes("PERCENTAGE") && (
                            <ToggleButton id="type-percentage" value="PERCENTAGE" variant="outline-secondary">
                                Percentage
                            </ToggleButton>
                        )}
                        {allowedSeasons.includes("FIXED") && (
                            <ToggleButton id="type-fixed" value="FIXED" variant="outline-secondary">
                                Fixed amount
                            </ToggleButton>
                        )}
                    </ToggleButtonGroup>
                </Form.Group>

                {/* Value */}
                {NEED_VALUE.has(season) && (
                    <Form.Group className="mb-3">
                        <Form.Label className="field-label d-block mb-2">{valueLabel}</Form.Label>
                        <div className="d-flex align-items-start gap-3 flex-wrap">
                            <Form.Control
                                type="number"
                                step={valueStep}
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                style={{ width: 260 }}
                                placeholder={season === "PERCENTAGE" ? "e.g. 12.5" : "e.g. 15.00"}
                            />
                            <div className="text-muted" style={{ fontSize: 12, maxWidth: 420 }}>
                                {valueHelp}
                                {!valueValid && <div className="text-danger mt-1">Please enter a valid non-negative number.</div>}
                            </div>
                        </div>
                    </Form.Group>
                )}
            </Offcanvas.Body>

            <div className="drawer-footer">
                <Button variant="outline-secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleConfirm} disabled={!canSubmit}>
                    Save
                </Button>
            </div>
        </Offcanvas>
    );
};

export default ShopSeasonDrawer;
